import React from 'react'
import { withFirebase } from '../../components/Fb'

// reactstrap components
import { Row, Col, Button } from 'reactstrap'
import FalconModal from '../../components/Inputs/FalconModal'
import { getMembershipSchema, getMembershipUiSchema } from '../../components/Inputs/dataschemas'
// import ReactTable from 'react-table'
import ReactTable from '../../components/Lists/ReactTableWrapper'
import { filterCaseInsensitive, getTdProps } from '../../components/FalconTable'
import FalconLoading from '../../components/Inputs/FalconLoading'
import EditButton from '../../components/Buttons/EditButton'
import DeleteButton from '../../components/Buttons/DeleteButton'
import Infobox from '../../components/Infos/Infobox'
import Box from '@mui/material/Box'
import { DEFAULT_SPORTS, DEFAULT_TYPES } from '../../sports'
import { benefitsSummary, mergeSports } from '../../components/utils/memberships'
import { imageThumbnailUrl } from '../../components/utils/images'

// core components

// Finds a benefit's dependency branch by its own discriminator.
//
// This used to be a positional index - oneOf[2] for 'discount' - which landed on the right
// branch only because two dead branches (earlybird, stalkerservice, commented out of the
// selectable anyOf but still present here) padded the array. Adding a benefit type in the
// wrong place, or ever tidying those two away, silently repointed the injection at another
// benefit and the dropdown came up empty with no error.
const benefitBranch = (schema, benefittype) => {
  const branches = schema.properties.benefits.items.dependencies.benefittype.oneOf
  return branches.find(
    (branch) =>
      branch.properties &&
      branch.properties.benefittype &&
      (branch.properties.benefittype.enum || []).includes(benefittype)
  )
}

class MembershipsContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = { loading: true, membershipModalOpen: false, memberships: null, images: [], providers: [], sports: DEFAULT_SPORTS, activitytypes: DEFAULT_TYPES }
  }

  componentDidMount() {
    this.updateData()
  }

  updateData = () => {
    Promise.all([this.props.firebase.getMemberships(), this.props.firebase.getMembershipResourcePricings(), this.props.firebase.getProviders()]).then(
      (values) => {
        const memberships = []
        if (!values[0].empty) {
          // eslint-disable-next-line
          values[0].docs.map((doc) => {
            if (doc.data().state !== 'deleted') {
              memberships.push({ ...doc.data(), id: doc.id })
            }
          })
        }
        if (!values[1].empty) {
          let _d = values[1].docs
            .map((sh) => {
              return { ...sh.data(), id: sh.id }
            })
            .sort((a, b) => {
              return (b.priority || 0) - (a.priority || 0)
            })
          this.setState(
            {
              pricings: _d,
              raw: memberships,
            },
            this.filterData
          )
        } else {
          this.setState({ raw: memberships }, this.filterData)
        }
        if(!values[2].empty) {
          const providers = []
          // eslint-disable-next-line
          values[2].docs.map((doc) => {
            if (doc.data().state !== 'deleted') {
              providers.push({ ...doc.data(), id: doc.id })
            }
          })
          this.setState({ providers })
        }
      }
    )
    this.props.firebase.getImages().then((images) => {
      this.setState({ images })
    })
    this.loadSports()
    this.loadActivityTypes()
  }

  // getSports() returns a DocumentReference, so it needs .get() - without it doc.exists is
  // undefined and the venue's own sports never merge in.
  loadSports = () => {
    this.props.firebase
      .getSports()
      .get()
      .then((doc) => {
        const list = doc.exists && doc.data() ? doc.data().list : []
        this.setState({ sports: mergeSports(DEFAULT_SPORTS, list) }, this.filterData)
      })
      .catch((error) => console.error('Sports failed to load', error))
  }

  // Same shape and the same DocumentReference caveat as getSports().
  loadActivityTypes = () => {
    this.props.firebase
      .getActivitytypes()
      .get()
      .then((doc) => {
        const list = doc.exists && doc.data() ? doc.data().list : []
        this.setState({ activitytypes: mergeSports(DEFAULT_TYPES, list) }, this.filterData)
      })
      .catch((error) => console.error('Activity types failed to load', error))
  }

  handleRowSelect = (e) => {
    const _result = this.state.memberships.filter((r) => r.id === e)
    this.setState({ selectedResource: _result[0] }, this.toggleMembershipModal)
  }

  filterData = async () => {
    if (!this.state.raw) return
    const data = []
    for (let i = 0; i < this.state.raw.length; i++) {
      data.push({
        ...this.state.raw[i],
        benefitssummary: benefitsSummary(this.state.raw[i], this.state.sports, this.state.activitytypes),
      })
    }
    this.setState({ loading: false, memberships: data })
  }

  deleteMembership = async (id) => {
    await this.props.firebase.deleteMembership(id)
    this.setState({ loading: true }, this.updateData)
  }

  addNewMembership = () => {
    this.setState({ selectedResource: {} }, this.toggleMembershipModal)
  }

  toggleMembershipModal = () => {
    this.setState({
      membershipModalOpen: !this.state.membershipModalOpen,
    })
  }

  getEnhancedMembershipSchema = () => {
    const schema = getMembershipSchema()

    // Membership pricelists, into the 'discount' benefit.
    const discountBranch = benefitBranch(schema, 'discount')
    if (discountBranch && this.state.pricings) {
      const pricing = { ...discountBranch.properties.pricing }
      for (let i = 0; i < this.state.pricings.length; i++) {
        pricing.oneOf.push({ const: this.state.pricings[i].id, title: this.state.pricings[i].name })
      }
      discountBranch.properties.pricing = pricing
    }

    // Sports, into the 'activitydiscount' benefit. Keys as the stored value, labels as the
    // shown title - see the note in schemas/membership.js for why keys.
    const activityDiscountBranch = benefitBranch(schema, 'activitydiscount')
    if (activityDiscountBranch && this.state.sports) {
      const items = { ...activityDiscountBranch.properties.sports.items }
      items.oneOf = this.state.sports.map((sport) => ({ const: sport.key, title: sport.label }))
      activityDiscountBranch.properties.sports.items = items
    }

    // Sports and activity types, into the 'activityentries' quota. Keys stored, labels
    // shown - activity.sport and activity.type both hold the LABEL, so the backend maps
    // back through config/sports and config/activitytypes.
    const entriesBranch = benefitBranch(schema, 'activityentries')
    if (entriesBranch) {
      if (this.state.sports) {
        entriesBranch.properties.sports.items = {
          ...entriesBranch.properties.sports.items,
          oneOf: this.state.sports.map((sport) => ({ const: sport.key, title: sport.label })),
        }
      }
      if (this.state.activitytypes) {
        entriesBranch.properties.activitytypes.items = {
          ...entriesBranch.properties.activitytypes.items,
          oneOf: this.state.activitytypes.map((type) => ({ const: type.key, title: type.label })),
        }
      }
    }

    const provider = { ...schema.dependencies.provideritem.oneOf[0].properties.provider }
    if (this.state.providers) {
      for (let i = 0; i < this.state.providers.length; i++) {
        provider.oneOf.push({ const: this.state.providers[i].code, title: this.state.providers[i].name })
      }
      schema.dependencies.provideritem.oneOf[0].properties.provider = provider
    }
    if (this.state.images) {
      schema.dependencies.availableinshop.oneOf[0].properties.image.items.images = this.state.images
    }
    return schema
  }

  createMembershipModal = () => {
    return (
      <FalconModal
        onUpdate={this.props.firebase.updateMembership}
        onCreate={this.props.firebase.createMembership}
        modifiedCallback={this.updateData}
        toggle={this.toggleMembershipModal}
        resource={this.state.selectedResource}
        isOpen={this.state.membershipModalOpen}
        uiSchema={getMembershipUiSchema()}
        dataSchema={this.getEnhancedMembershipSchema()}
        title={this.state.selectedResource && this.state.selectedResource.id ? 'Muokkaa jäsenyyttä' : 'Uusi jäsenyys'}
        size='lg'
      />
    )
  }

  render() {
    if (this.state.loading) {
      return <FalconLoading />
    } else {
      return (
        <>
          <div className='content'>
            <Row>
              <Col>
                <Button onClick={this.addNewMembership}>Lisää jäsenyys</Button>
              </Col>
              <Col>
                <Infobox index={'0'} sx={{ mb: 1.25 }} />
              </Col>
            </Row>
            <Row>
              <Col md='12'>
                <ReactTable
                  data={this.state.memberships}
                  filterable
                  noSumRow={true}
                  noDataText='Ei löytynyt yhtään jäsenyyttä'
                  defaultFilterMethod={(filter, row) => filterCaseInsensitive(filter, row)}
                  columns={[
                    {
                      Header: 'Kuva',
                      accessor: 'image',
                      filterable: false,
                      sortable: false,
                      width: 60,
                      renderCell: (params) => {
                        const src = imageThumbnailUrl(params.row.image)
                        if (!src) return null
                        return (
                          <Box
                            component='img'
                            src={src}
                            alt={params.row.name || ''}
                            sx={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 0.5, display: 'block' }}
                          />
                        )
                      },
                    },
                    {
                      Header: 'Nimi',
                      accessor: 'name',
                    },
                    {
                      Header: 'Edut',
                      accessor: 'benefitssummary',
                    },
                    {
                      Header: 'Ostettavissa verkosta',
                      accessor: 'availableinshop',
                      valueFormatter: (value) => {
                        return value === 'true' ? 'Kyllä' : 'Ei'
                      }
                    },
                    {
                      Header: 'Kumppanituote',
                      accessor: 'provideritem',
                      valueFormatter: (value) => {
                        return value ? 'Kyllä' : 'Ei'
                      }
                    },
                    {
                      Header: 'Muokkaa',
                      accessor: 'edit',
                      filterable: false,
                      sortable: false,
                      width: 120,
                      renderCell: (params) => {
                        return (
                          <EditButton
                            onClick={(el) => {
                              this.handleRowSelect(params.row.id)
                            }}
                          />
                        )
                      },
                    },
                    {
                      Header: 'Poista',
                      accessor: 'actions',
                      filterable: false,
                      sortable: false,
                      width: 100,
                      renderCell: (params) => {
                        return (
                          <DeleteButton
                            onClick={(el) => {
                              this.deleteMembership(params.row.id)
                            }}
                          />
                        )
                      },
                    },
                  ]}
                  defaultPageSize={10}
                  showPaginationTop
                  showPaginationBottom={false}
                  getTdProps={(state, rowInfo, col) => getTdProps(rowInfo, col, this.handleRowSelect)}
                  className='-striped -highlight primary-pagination'
                />
              </Col>
            </Row>
            {this.state.alert}
            {this.createMembershipModal()}
          </div>
        </>
      )
    }
  }
}

export default withFirebase(MembershipsContainer)
